// Shared front-end utilities. Loaded (defer) BEFORE other scripts so
// window.theme exists when product-form.js / cart-drawer.js / etc. run.
window.theme = window.theme || {};
(function (t) {
  async function parse(res) {
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw Object.assign(new Error(data.description || data.message || "Request failed"), { data, status: res.status });
    }
    return data;
  }

  t.postForm = function (url, formData) {
    return fetch(url, { method: "POST", body: formData, headers: { Accept: "application/json" } }).then(parse);
  };

  t.postJson = function (url, body) {
    return fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify(body),
    }).then(parse);
  };

  t.sectionNode = function (html, selector) {
    if (!html) return null;
    return new DOMParser().parseFromString(html, "text/html").querySelector(selector);
  };

  t.updateCartCount = function (count) {
    const n = Number(count) || 0;
    document.querySelectorAll("[data-cart-count]").forEach(function (el) {
      el.textContent = n;
      el.hidden = n === 0;
    });
  };

  t.FOCUSABLE =
    'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

  t.trapFocus = function (container) {
    function items() {
      return Array.prototype.slice
        .call(container.querySelectorAll(t.FOCUSABLE))
        .filter(function (el) { return el.offsetParent !== null; });
    }
    function onKey(e) {
      if (e.key !== "Tab") return;
      const list = items();
      if (!list.length) return;
      const first = list[0];
      const last = list[list.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
    container.addEventListener("keydown", onKey);
    const firstEl = items()[0];
    if (firstEl) firstEl.focus();
    return function release() { container.removeEventListener("keydown", onKey); };
  };

  // Quantity stepper (input mode): +/- buttons adjust the adjacent number input.
  document.addEventListener("click", function (e) {
    const step = e.target.closest("[data-qty-step]");
    if (!step) return;
    const wrap = step.parentElement;
    const input = wrap && wrap.querySelector('input[type="number"]');
    if (!input) return;
    const min = Number(input.min) || 0;
    const next = (Number(input.value) || 0) + Number(step.dataset.qtyStep);
    input.value = Math.max(min, next);
    input.dispatchEvent(new Event("input", { bubbles: true }));
    input.dispatchEvent(new Event("change", { bubbles: true }));
  });

  // Localization selects auto-submit their form on change.
  document.addEventListener("change", function (e) {
    const sel = e.target.closest("form[data-localization] select");
    if (sel && sel.form) sel.form.submit();
  });

  // Order-note autosave. Delegated at the document so the cart page and the cart drawer
  // share one implementation — previously it lived on <cart-items>, so the drawer had no
  // way to set a note at all. This is the only surface that can: Shopify's checkout has
  // no theme-customisable note field, and cart.note becomes the order's additional
  // details in the admin.
  document.addEventListener("change", function (e) {
    const note = e.target.closest("[data-cart-note]");
    if (!note) return;
    // The cart page and the drawer are both on screen at /cart, so the other field is
    // showing whatever cart.note was at page load. Mirroring the new value stops an
    // untouched stale field from overwriting this edit if the shopper types in both.
    document.querySelectorAll("[data-cart-note]").forEach(function (el) {
      if (el !== note) el.value = note.value;
    });
    t.postJson(window.routes.cart_url + "/update.js", { note: note.value }).catch(function () {});
  });

  // Discount codes. The Ajax Cart API has no discount field, so a code is applied by
  // navigating to /discount/<code>?redirect=<path> — the route behind shareable discount
  // links. Shopify validates it, attaches it to the session and reflects it back through
  // cart.cart_level_discount_applications; an invalid code is silently ignored, so the
  // rendered "applied" list is the feedback. See snippets/cart-discount.liquid.
  document.addEventListener("submit", function (e) {
    const form = e.target.closest("[data-discount-form]");
    if (!form) return;
    e.preventDefault();
    const input = form.querySelector("[data-discount-input]");
    const code = ((input && input.value) || "").trim();
    if (!code) return;
    // Locale/market path prefix: the same root the product URLs are built from.
    const root = ((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/").replace(/\/$/, "");
    const back = form.dataset.redirect || window.location.pathname;
    window.location.href =
      root + "/discount/" + encodeURIComponent(code) + "?redirect=" + encodeURIComponent(back);
  });

  // Native share (falls back to copying the URL).
  document.addEventListener("click", function (e) {
    const s = e.target.closest("[data-share]");
    if (!s) return;
    const data = { title: s.dataset.title, url: s.dataset.url };
    if (navigator.share) navigator.share(data).catch(function () {});
    else if (navigator.clipboard) navigator.clipboard.writeText(s.dataset.url);
  });

  // Loading helper: toggle .is-loading + aria-busy + disabled together.
  t.setLoading = function (el, on) {
    if (!el) return;
    el.classList.toggle("is-loading", !!on);
    el.setAttribute("aria-busy", on ? "true" : "false");
    if ("disabled" in el) {
      if (on) { el.dataset.wasDisabled = el.disabled ? "1" : "0"; el.disabled = true; }
      else { el.disabled = el.dataset.wasDisabled === "1"; delete el.dataset.wasDisabled; }
    }
  };

  // Skeleton HTML generator. Mirrors snippets/skeleton.liquid; both use .jg-skel*
  // component classes + inline styles so the markup survives Tailwind purging.
  t.skeleton = function (variant, count) {
    count = count || 1;
    var one;
    switch (variant) {
      case "product-card":
        one =
          '<div class="jg-skel-card"><div class="jg-skel jg-skel-card__media"></div>' +
          '<div class="jg-skel-card__body">' +
          '<div class="jg-skel jg-skel-line" style="width:33%"></div>' +
          '<div class="jg-skel jg-skel-line" style="width:85%"></div>' +
          '<div class="jg-skel jg-skel-line" style="width:60%"></div>' +
          '<div class="jg-skel jg-skel-line" style="width:45%;height:24px;margin:6px 0"></div>' +
          '<div class="jg-skel jg-skel-line" style="width:100%;height:44px;margin-top:auto"></div>' +
          "</div></div>";
        break;
      case "cart-item":
        one =
          '<div class="jg-skel-cart-item"><div class="jg-skel jg-skel-cart-item__thumb"></div>' +
          '<div style="flex:1;min-width:0;display:flex;flex-direction:column;gap:8px">' +
          '<div class="jg-skel jg-skel-line" style="width:30%;height:10px"></div>' +
          '<div class="jg-skel jg-skel-line" style="width:75%"></div>' +
          '<div class="jg-skel jg-skel-line" style="width:35%;height:18px"></div>' +
          '<div class="jg-skel jg-skel-line" style="width:96px;height:32px"></div>' +
          "</div></div>";
        break;
      case "pdp":
        one =
          '<div class="jg-skel-pdp"><div class="jg-skel jg-skel-pdp__media"></div>' +
          '<div class="jg-skel-pdp__col">' +
          '<div class="jg-skel jg-skel-line" style="width:40%;height:12px"></div>' +
          '<div class="jg-skel jg-skel-line" style="width:80%;height:28px"></div>' +
          '<div class="jg-skel jg-skel-line" style="width:30%;height:24px"></div>' +
          '<div class="jg-skel jg-skel-line" style="width:100%;height:44px;margin-top:12px"></div>' +
          "</div></div>";
        break;
      default:
        one = '<div class="jg-skel jg-skel-line"></div>';
    }
    return new Array(count + 1).join(one);
  };

  // ── Server-rendered product cards ──
  // Client-side surfaces (wishlist, recently viewed) render real _product-card
  // markup by fetching /products/<handle>?view=card rather than templating in JS.
  // That keeps prices, stock, add-to-cart, the heart and quick-view all live and
  // identical to every other card, at the cost of one small request per item.

  // Build a product URL from a handle, honouring a locale/market path prefix.
  t.productUrl = function (handle) {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
    return root.replace(/\/$/, "") + "/products/" + encodeURIComponent(handle);
  };

  function fetchCard(item) {
    const u = new URL(item.url, window.location.origin);
    u.searchParams.set("view", "card");
    return fetch(u.pathname + u.search).then(function (r) {
      if (r.status === 404) return { id: item.id, gone: true, node: null };
      if (!r.ok) throw new Error("HTTP " + r.status);
      return r.text().then(function (html) {
        // Unwrap: [data-card-fragment] is just the transport wrapper. Appending it
        // would make an unstyled div the grid item, leaving the real .jg-card a
        // block child of it — which shrink-wraps to content width under
        // justify-items:start. The card itself has to be the grid/flex item.
        const wrap = t.sectionNode(html, "[data-card-fragment]");
        return { id: item.id, gone: false, node: wrap && wrap.firstElementChild };
      });
    });
  }

  // items: [{ url, id }]. Shows a loading state while in flight, then replaces it
  // with the fetched cards IN INPUT ORDER (allSettled preserves it). Resolves with
  // { rendered, gone } — `gone` holds the ids that 404'd, so the caller can prune
  // them from its own storage.
  //
  // opts.loader: "skeleton" (default) paints dimension-matched card skeletons for
  // zero CLS — right for grids. "spinner" paints one centred circular loader for
  // the whole container — right for a narrow drawer panel, where a column of card
  // skeletons is busier than the content it stands in for.
  t.renderProductCards = function (container, items, opts) {
    if (!container) return Promise.resolve({ rendered: 0, gone: [] });
    if (!items.length) { container.innerHTML = ""; return Promise.resolve({ rendered: 0, gone: [] }); }

    container.setAttribute("aria-busy", "true");
    container.innerHTML =
      (opts && opts.loader) === "spinner"
        ? '<span class="jg-loader jg-loader--panel" aria-hidden="true"></span>'
        : t.skeleton("product-card", items.length);

    return Promise.allSettled(items.map(fetchCard)).then(function (results) {
      container.innerHTML = "";
      const gone = [];
      let rendered = 0;
      results.forEach(function (res) {
        if (res.status !== "fulfilled") return;
        if (res.value.gone) { gone.push(res.value.id); return; }
        if (!res.value.node) return;
        container.appendChild(res.value.node);
        rendered += 1;
      });
      container.removeAttribute("aria-busy");
      // Injected cards carry an unpainted wishlist heart: wishlist.js paints on load and
      // on click, neither of which covers markup appended after a fetch. Guarded because
      // wishlist.js is a later <script> — by the time this runs (post-DOMContentLoaded)
      // it is defined, but the order is not worth depending on.
      if (t.paintWishlist) t.paintWishlist();
      return { rendered: rendered, gone: gone };
    });
  };

  // Image fade-in: mark loaded so CSS fades opacity 0→1. `load` doesn't bubble,
  // so listen in capture on document (catches dynamically injected images too).
  // .jg-load gets the same .is-loaded mark WITHOUT the fade: an image that sits at
  // opacity 0 until it loads is not a Largest Contentful Paint candidate, so a hero that
  // must count as the page's LCP (the collection banner) cannot use .jg-fade. It still
  // needs the mark, because that is what drops its loading shimmer.
  function markImgLoaded(img) { if (img && img.tagName === "IMG") img.classList.add("is-loaded"); }
  document.addEventListener("load", function (e) {
    var t = e.target;
    if (t && t.tagName === "IMG" && (t.classList.contains("jg-fade") || t.classList.contains("jg-load"))) markImgLoaded(t);
  }, true);
  function sweepImages() {
    document.querySelectorAll("img.jg-fade, img.jg-load").forEach(function (img) {
      if (img.complete && img.naturalWidth > 0) markImgLoaded(img);
    });
  }
  document.addEventListener("DOMContentLoaded", sweepImages);
  window.addEventListener("load", sweepImages);
})(window.theme);
